/**
 * LiveBrowserCanvas — paints a user's live Chromium framebuffer inside
 * LexPortal (P4.D.X, the PLATFORM_ADMIN cross-user view). Thin wrapper around
 * `@novnc/novnc`'s `RFB`, mirroring lexide's proven `NoVncPane` lifecycle
 * (P4.D.W) with two cross-user adaptations:
 *
 *   1. The tunnel path carries the VIEWED user's id
 *      (`/ws/runtime/<targetUserId>/api/lx/openclaw/browser/vnc/ws`), built
 *      here from the `targetUserId` prop — lexserver's VNC branch authorises
 *      the operator via the `platform.admin` scope in the subprotocol JWT and
 *      routes to that user's runtime (+ writes an audit row).
 *   2. The `?token=` ticket is minted cross-user via `getVncTicket(getToken,
 *      targetUserId)` (the lexserver proxy forwards it with `X-Target-User-Id`
 *      to the viewed user's runtime, which writes it to ITS vnc-tokens.txt).
 *
 * Security (matches lexide §10): the ticket NEVER lands in localStorage, the
 * page URL, a console.log, an error toast, or a Sentry breadcrumb — it lives in
 * a ref + the WS URL handed to RFB. The operator-warning banner is rendered by
 * the parent (NoVncPane). See ai-docs/lexruntime-noVNC-monkey-patch.md.
 */

import { useEffect, useRef, useState } from "react";
// @ts-expect-error — @novnc/novnc ships ESM with no bundled .d.ts. Call shape
// `new RFB(target, url, options?)` + `disconnect()`, stable since v1.4.
import RFB from "@novnc/novnc";
import { getVncTicket } from "@/api/lx-openclaw-config";
import { getLexiunToken } from "@/lib/lexiun-jwt";
import { buildVncWsUrl } from "@/lib/vnc-url";

export type LiveBrowserStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

export interface LiveBrowserCanvasProps {
  /** The VIEWED user (the runtime whose Chromium we stream), NOT the operator. */
  targetUserId: string;
  /** Runtime-relative noVNC path from `getBrowserStatus().vnc.endpoint`
   *  (e.g. `/api/lx/openclaw/browser/vnc/ws`). We prefix the lexserver tunnel
   *  segment `/ws/runtime/<targetUserId>` and resolve against the lexserver
   *  WS origin in `buildVncWsUrl`. */
  vncEndpoint: string;
  onStatusChange?: (status: LiveBrowserStatus, detail?: string) => void;
  className?: string;
}

/** Build the lexserver-tunneled, target-prefixed path from the runtime-relative
 *  endpoint. Idempotent if the endpoint is already tunnel-prefixed. */
function tunnelPath(targetUserId: string, vncEndpoint: string): string {
  if (vncEndpoint.startsWith("/ws/runtime/")) return vncEndpoint;
  return `/ws/runtime/${encodeURIComponent(targetUserId)}${vncEndpoint}`;
}

export function LiveBrowserCanvas({
  targetUserId,
  vncEndpoint,
  onStatusChange,
  className,
}: LiveBrowserCanvasProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rfbRef = useRef<any>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<LiveBrowserStatus>("idle");
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const statusRef = useRef<LiveBrowserStatus>("idle");

  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);
  function setStatusAndNotify(next: LiveBrowserStatus, detail?: string) {
    statusRef.current = next;
    setStatus(next);
    setErrorDetail(detail ?? null);
    onStatusChangeRef.current?.(next, detail);
  }

  function clearRefresh() {
    if (refreshTimerRef.current !== null) {
      window.clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }

  useEffect(() => {
    let cancelled = false;
    const mount = mountRef.current;
    if (!mount) return;
    let connectTimer: ReturnType<typeof setTimeout> | null = null;

    /** Re-mint the ticket every `ttlMs - 10s` so the next reconnect uses a
     *  fresh token (mid-session replacement isn't an upstream API). */
    function scheduleRefresh(ttlMs: number) {
      clearRefresh();
      const intervalMs = Math.max(15_000, ttlMs - 10_000);
      refreshTimerRef.current = window.setInterval(() => {
        void getVncTicket(getLexiunToken, targetUserId).catch(() => {
          // Soft-fail: keep the still-valid ticket until expiry; the next RFB
          // disconnect surfaces to the operator via the status indicator.
        });
      }, intervalMs);
    }

    async function start() {
      if (cancelled) return;
      setStatusAndNotify("connecting");
      let token: string;
      let ttlMs: number;
      try {
        const ticket = await getVncTicket(getLexiunToken, targetUserId);
        token = ticket.token;
        ttlMs = ticket.ttlMs;
      } catch (err) {
        if (cancelled) return;
        setStatusAndNotify("error", err instanceof Error ? err.message : String(err));
        return;
      }
      if (cancelled) return;
      const wsUrl = buildVncWsUrl(tunnelPath(targetUserId, vncEndpoint), token);
      // The operator's Lexiun JWT (carries `platform.admin`) rides in the
      // subprotocol so lexserver's VNC branch authorises the cross-user view;
      // websockify validates the `?token=` separately.
      const lexiunJwt = await getLexiunToken();
      if (cancelled) return;
      if (!lexiunJwt) {
        setStatusAndNotify("error", "no-operator-token");
        return;
      }
      try {
        const rfb = new RFB(mount, wsUrl, {
          wsProtocols: [`lexiun-jwt.${lexiunJwt}`, "binary"],
        });
        rfb.scaleViewport = true;
        rfb.resizeSession = false;
        rfb.background = "transparent";
        rfb.addEventListener("connect", () => {
          if (cancelled) return;
          setStatusAndNotify("connected");
          scheduleRefresh(ttlMs);
        });
        rfb.addEventListener("disconnect", (ev: { detail?: { clean?: boolean } }) => {
          if (cancelled) return;
          setStatusAndNotify(
            ev?.detail?.clean ? "disconnected" : "error",
            ev?.detail?.clean ? undefined : "websocket-closed",
          );
          clearRefresh();
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rfb.addEventListener("securityfailure", (e: any) => {
          if (cancelled) return;
          setStatusAndNotify("error", `security-failure:${e?.detail?.status ?? "?"}`);
        });
        rfb.addEventListener("credentialsrequired", () => {
          if (cancelled) return;
          setStatusAndNotify("error", "credentials-required");
        });
        rfbRef.current = rfb;
        connectTimer = setTimeout(() => {
          if (cancelled) return;
          if (statusRef.current === "connecting") {
            setStatusAndNotify("error", "connect-timeout");
            try {
              rfb.disconnect();
            } catch {
              /* RFB may not yet have a WS to close */
            }
          }
        }, 30_000);
      } catch (err) {
        if (cancelled) return;
        setStatusAndNotify("error", err instanceof Error ? err.message : String(err));
      }
    }

    void start();

    return () => {
      cancelled = true;
      clearRefresh();
      if (connectTimer !== null) {
        clearTimeout(connectTimer);
        connectTimer = null;
      }
      try {
        rfbRef.current?.disconnect();
      } catch {
        /* RFB raises if not yet connected — tolerate. */
      }
      rfbRef.current = null;
    };
    // Restart cleanly when the viewed user or the endpoint changes.
  }, [targetUserId, vncEndpoint]);

  return (
    <div
      data-testid="novnc-live-canvas"
      data-status={status}
      data-error-detail={errorDetail ?? undefined}
      className={className}
    >
      <div ref={mountRef} tabIndex={0} className="h-full w-full focus:outline-none" />
    </div>
  );
}
